import { useState, useEffect, useRef } from 'react';
import { Loader2, Sparkles, RefreshCw, Copy, Check, ChevronDown } from 'lucide-react';
import { storyboardApi, copyToClipboard } from '../storyboardApi';
import { CopyCard } from './CopyCard';
import { AgentAssetCard } from './AgentAssetCard';

const fmtList = (arr, fn) => (arr || []).map(fn).join('\n');

function buildModules(board) {
  const mp = board.marketing_pack?.status === 'ready' ? board.marketing_pack.data : null;
  const mk = board.mohai_kit?.status === 'ready' ? board.mohai_kit.data : null;
  const st = board.strategy || null;
  const imgs = mk?.agent_images || [];
  const agentImagesText = fmtList(imgs, (im, i) =>
    `Asset ${i + 1}: ${im.title || ''}\n· Objective: ${im.objective}\n· When: ${im.when_to_show}\n· Trigger: ${im.trigger_message}\n· Master Prompt snippet: ${im.master_prompt_snippet || ''}`);
  const buttonsText = fmtList(mk?.buttons, b => `[${b.type}] ${b.label} — needs: ${b.value_needed}`);
  const checklistText = fmtList(mk?.missing_fields, f => `☐ ${f.field}: ${f.why}`);
  const animText = mp ? `Style: ${mp.animation_style?.recommendation || ''} — ${mp.animation_style?.reasoning || ''}\n\n${mp.animation_master_prompt || ''}` : null;
  return [
    { t: 'MOHAI Master Prompt', text: mk?.master_prompt, collapsible: true, id: 'master-prompt' },
    { t: 'MOHAI Agent Assets', text: agentImagesText || null, id: 'agent-assets', images: imgs },
    { t: 'Suggested Buttons', text: buttonsText || null, id: 'buttons' },
    { t: 'Facebook Post', text: mp?.posts?.facebook, id: 'facebook' },
    { t: 'Instagram Post', text: mp?.posts?.instagram, id: 'instagram' },
    { t: 'TikTok Post', text: mp?.posts?.tiktok, id: 'tiktok' },
    { t: 'X Post', text: mp?.posts?.x, id: 'x' },
    ...(mp?.posts?.linkedin ? [{ t: 'LinkedIn Post', text: mp.posts.linkedin, id: 'linkedin' }] : []),
    { t: 'Caption ES', text: mp?.caption_es, id: 'caption-es' },
    { t: 'Caption EN', text: mp?.caption_en, id: 'caption-en' },
    { t: 'CTA', text: mp?.cta, id: 'cta' },
    { t: 'Hashtags', text: (mp?.hashtags || []).join(' ') || null, id: 'hashtags' },
    { t: 'Voice Script ES (15-20s)', text: mp?.voice_script_es, id: 'script-es' },
    { t: 'Voice Script EN (15-20s)', text: mp?.voice_script_en, id: 'script-en' },
    { t: 'ElevenLabs Prompt', text: mp?.elevenlabs_prompt, id: 'elevenlabs' },
    { t: 'Suno Prompt', text: mp?.suno_prompt, id: 'suno' },
    { t: 'Animation Prompt', text: animText, id: 'animation' },
    { t: 'Location', text: mk?.contact?.location || (mk ? 'Pending — complete before launch' : null), id: 'location' },
    { t: 'Phone', text: mk?.contact?.phone || (mk ? 'Pending — complete before launch' : null), id: 'phone' },
    { t: 'Website', text: mk?.contact?.website || (mk ? 'Pending — complete before launch' : null), id: 'website' },
    { t: 'Missing Data Checklist', text: checklistText || null, id: 'checklist' },
  ].map(m => ({ ...m, strategySummary: st }));
}

export default function CampaignPackage({ board, authHeader, onBoardChange }) {
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState('');
  const [copiedAll, setCopiedAll] = useState(false);
  const [showFullIntel, setShowFullIntel] = useState(false);
  const pollRef = useRef(null);

  const mpStatus = board.marketing_pack?.status;
  const mkStatus = board.mohai_kit?.status;
  const generating = mpStatus === 'generating' || mkStatus === 'generating';
  const ready = mpStatus === 'ready' && mkStatus === 'ready';
  const hasAny = !!(board.marketing_pack || board.mohai_kit);
  const failed = mpStatus === 'failed' || mkStatus === 'failed';
  const strategy = board.strategy;

  useEffect(() => {
    if (!generating) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const { ok, data } = await storyboardApi.get(board.id, authHeader);
      if (ok) onBoardChange(data.storyboard);
    }, 8000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generating, board.id]);

  const launch = async () => {
    if (hasAny && !generating) {
      if (!window.confirm('This will regenerate the whole Campaign Package (Brief, Concept and Scenes stay untouched). Continue?')) return;
    }
    setError('');
    setLaunching(true);
    const { ok, data } = await storyboardApi.campaignPackage(board.id, authHeader);
    setLaunching(false);
    if (ok) {
      const mark = { status: 'generating' };
      onBoardChange({ ...board, marketing_pack: mark, mohai_kit: mark });
    } else {
      setError(typeof data?.detail === 'string' ? data.detail : 'Campaign Package failed to start');
    }
  };

  const modules = buildModules(board);
  const copyAll = async () => {
    const full = modules
      .map((m, i) => `## ${String(i + 1).padStart(2, '0')} · ${m.t}\n${m.text || '—'}`)
      .join('\n\n');
    await copyToClipboard(`RUIDO.AI — CAMPAIGN PACKAGE: ${board.title || ''}\n\n${full}`);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 1600);
  };

  return (
    <div className="pt-6 mt-2 border-t border-gray-200/60 space-y-4" data-testid="campaign-package">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Campaign Package</h3>
          <p className="text-xs text-gray-400 mt-0.5">Everything ready to copy into MOHAI, in order.</p>
        </div>
        <button
          data-testid="cp-generate-btn"
          onClick={launch}
          disabled={generating || launching}
          className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 transition-all active:scale-[0.98] disabled:opacity-40"
        >
          {generating || launching
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : hasAny ? <RefreshCw className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
          <span>{launching ? 'Starting...' : generating ? 'Generating (2-6 min)...' : hasAny ? 'Regenerate Campaign Package' : 'Generate Campaign Package'}</span>
        </button>
      </div>

      {error && <p className="text-xs text-red-500" data-testid="cp-error">{error}</p>}
      {failed && (
        <p className="text-xs text-red-500" data-testid="cp-job-error">
          {board.marketing_pack?.error || board.mohai_kit?.error || 'Generation failed'} — retry with the button above.
        </p>
      )}

      {/* Campaign Intelligence summary */}
      {strategy?.summary_points?.length > 0 && (
        <div className="rounded-2xl border border-gray-200/50 bg-white/40 p-4" data-testid="cp-intelligence">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-900">Campaign Intelligence</p>
            <button
              data-testid="cp-intel-toggle"
              onClick={() => setShowFullIntel(v => !v)}
              className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-700 transition-colors"
            >
              View full analysis <ChevronDown className={`w-3 h-3 transition-transform ${showFullIntel ? 'rotate-180' : ''}`} />
            </button>
          </div>
          <ul className="mt-2 space-y-1">
            {strategy.summary_points.slice(0, 10).map((p, i) => (
              <li key={i} className="text-xs text-gray-600 flex gap-2"><span className="text-gray-300">·</span>{p}</li>
            ))}
          </ul>
          {showFullIntel && (
            <div className="mt-3 pt-3 border-t border-gray-200/50 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {Object.entries(strategy).filter(([k]) => k !== 'summary_points').map(([k, v]) => (
                <div key={k}>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">{k.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-gray-600 whitespace-pre-wrap">
                    {typeof v === 'string' ? v : Array.isArray(v) ? v.join(' · ') : JSON.stringify(v)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {generating && !ready && (
        <div className="rounded-2xl border border-dashed border-gray-200/70 bg-white/30 p-8 text-center" data-testid="cp-generating">
          <Loader2 className="w-6 h-6 text-gray-300 mx-auto mb-2 animate-spin" />
          <p className="text-xs text-gray-400">Deep strategic reasoning in progress — modules appear as they finish.</p>
        </div>
      )}

      {/* Flat ordered modules */}
      {hasAny && !generating && (mpStatus === 'ready' || mkStatus === 'ready') && (
        <div className="space-y-2.5 pb-4">
          {modules.map((m, i) => (
            <CopyCard key={m.id} index={i + 1} title={m.t} text={m.text}
              collapsible={m.collapsible} testId={`cp-${m.id}`}>
              {m.id === 'agent-assets' && m.images?.length > 0 && (
                <div className="space-y-3">
                  <p className="text-[11px] text-gray-400 -mt-0.5">
                    Brand-new images designed exclusively for the MOHAI agent conversation — independent from the storyboard scenes.
                  </p>
                  {m.images.map((im, j) => (
                    <AgentAssetCard key={j} asset={im} index={j + 1} />
                  ))}
                </div>
              )}
            </CopyCard>
          ))}
          <div className="flex justify-center pt-3">
            <button
              data-testid="cp-copy-all"
              onClick={copyAll}
              className="flex items-center gap-1.5 px-6 py-2.5 rounded-xl text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 transition-all active:scale-[0.98]"
            >
              {copiedAll ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copiedAll ? 'Copied' : 'Copy Complete Campaign Package'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
